import React from 'react';
import { PagesProvider, PagesConfig } from '@kevlarcon/pages';
import apiClient from '../api/client';
import PartnerJoinGate from '../components/sections/PartnerJoinGate';
import { homeCustomComponents, homeSectionPreviews } from './customComponents';
import { listAvailableIcons, renderIcon } from './heroicons';

/**
 * Wires the reusable @kevlarcon/pages CMS to sgokit: the tenant axios client (so
 * the X-TENANT header callbox injects reaches the API), sgokit's design-system
 * class names for the render slots, and the gold/navy editor theme. Extra menu
 * link targets (built-in SPA routes) are supplied here rather than probed for by
 * the package.
 *
 * classNames map the package's render slots onto the OCEN tenant-site classes
 * (see styles/ocen.css) so CMS pages render at the site's width and rhythm.
 */
const config: PagesConfig = {
  apiClient,
  customComponents: homeCustomComponents,
  // What a section the server withheld (see pages.access) renders as, by rule.
  accessGates: { partner: PartnerJoinGate },
  previewComponents: homeSectionPreviews,
  iconOptions: listAvailableIcons(),
  renderIcon,
  classNames: {
    sectionWrapper: 'section',
    sectionInner: 'wrap',
    headingBlock: 'head',
    descriptionBlock: 'prose',
    headerlessWrapper: 'wrap',
    markdownContent: 'prose',
  },
  theme: {
    accent: '#ddab2b',      // --gold
    accentText: '#0d1c31',  // --navy-900
  },
  customLinkSources: async () => [
    { label: 'Donate', url: '/donate' },
  ],
  renderLoading: () => (
    <div className="wrap" style={{ padding: '5rem 1rem', textAlign: 'center' }}>
      Loading…
    </div>
  ),
  renderNotFound: () => (
    <div className="wrap" style={{ padding: '5rem 1rem', textAlign: 'center' }} data-testid="site-page-not-found">
      <h1>Page coming soon</h1>
      <p>This page hasn't been published yet.</p>
      <a className="btn btn-primary" href="/donate">Make a gift</a>
    </div>
  ),
  renderEmptySections: (onAddSection) => (
    <div className="wrap" style={{ padding: '5rem 1rem', textAlign: 'center' }} data-testid="site-page-empty-sections">
      <h2>This page has no sections yet</h2>
      <p>Add a section to start building it out.</p>
      <button
        type="button"
        className="btn btn-primary"
        onClick={onAddSection}
        data-testid="site-page-empty-sections-add-button"
      >
        Add a section
      </button>
    </div>
  ),
};

const SitePagesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <PagesProvider config={config}>{children}</PagesProvider>
);

export default SitePagesProvider;
